// Firebase Configuration for Hotel A.S.K Grand
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, get, onValue, update } from 'firebase/database';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAC6_oE5zjanqQGjSWUMhBpxXzmXzwwaMg",
  authDomain: "hotel-ask-grand.firebaseapp.com",
  databaseURL: "https://hotel-ask-grand-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "hotel-ask-grand",
  storageBucket: "hotel-ask-grand.firebasestorage.app",
  messagingSenderId: "181868409420",
  appId: "1:181868409420:web:c16ac005002ab4d3b6bae9"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);
const auth = getAuth(app);

// Room Management
export const roomsRef = ref(database, 'rooms');
export const bookingsRef = ref(database, 'bookings');
export const settingsRef = ref(database, 'settings');
export const analyticsRef = ref(database, 'analytics');

// Save room data
export async function saveRoom(roomNumber: string, roomData: any) {
  try {
    await set(ref(database, `rooms/${roomNumber}`), {
      ...roomData,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error saving room:', error);
    return false;
  }
}

// Get all rooms
export async function getRooms() {
  try {
    const snapshot = await get(roomsRef);
    if (snapshot.exists()) {
      return Object.values(snapshot.val());
    }
    return [];
  } catch (error) {
    console.error('Error getting rooms:', error);
    return [];
  }
}

// Listen to room changes in real-time
export function subscribeToRooms(callback: (rooms: any[]) => void) {
  return onValue(roomsRef, (snapshot) => {
    if (snapshot.exists()) {
      const roomsData = snapshot.val();
      const roomsArray = Object.values(roomsData);
      callback(roomsArray);
    } else {
      callback([]);
    }
  });
}

// Update room status
export async function updateRoomStatus(roomNumber: string, status: string, notes?: string) {
  try {
    await update(ref(database, `rooms/${roomNumber}`), {
      status,
      maintenance_notes: notes || '',
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating room status:', error);
    return false;
  }
}

// Check-in guest
export async function checkInGuest(roomNumber: string, guestData: any) {
  try {
    await update(ref(database, `rooms/${roomNumber}`), {
      status: 'occupied',
      current_guest: guestData,
      check_in: guestData.check_in,
      check_out: guestData.check_out,
      updatedAt: new Date().toISOString()
    });
    
    // Also save to bookings
    const bookingId = `booking_${Date.now()}`;
    await set(ref(database, `bookings/${bookingId}`), {
      id: bookingId,
      roomNumber,
      guestName: guestData.name,
      phone: guestData.phone,
      email: guestData.email,
      checkIn: guestData.check_in,
      checkOut: guestData.check_out,
      idProofType: guestData.id_proof_type || '',
      idProofNumber: guestData.id_proof || '',
      customPrice: guestData.custom_price || 0,
      specialRequests: guestData.special_requests || '',
      status: 'confirmed',
      createdAt: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error checking in guest:', error);
    return false;
  }
}

// Check-out guest
export async function checkOutGuest(roomNumber: string) {
  try {
    await update(ref(database, `rooms/${roomNumber}`), {
      status: 'cleaning',
      current_guest: null,
      check_in: null,
      check_out: null,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error checking out guest:', error);
    return false;
  }
}

// Save booking
export async function saveBooking(bookingData: any) {
  try {
    const bookingId = bookingData.id || `booking_${Date.now()}`;
    await set(ref(database, `bookings/${bookingId}`), {
      ...bookingData,
      id: bookingId,
      createdAt: bookingData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return bookingId;
  } catch (error) {
    console.error('Error saving booking:', error);
    return null;
  }
}

// Get all bookings
export async function getBookings() {
  try {
    const snapshot = await get(bookingsRef);
    if (snapshot.exists()) {
      return Object.values(snapshot.val());
    }
    return [];
  } catch (error) {
    console.error('Error getting bookings:', error);
    return [];
  }
}

// Subscribe to bookings in real-time
export function subscribeToBookings(callback: (bookings: any[]) => void) {
  return onValue(bookingsRef, (snapshot) => {
    if (snapshot.exists()) {
      const bookingsData = snapshot.val();
      const bookingsArray = Object.values(bookingsData);
      callback(bookingsArray);
    } else {
      callback([]);
    }
  });
}

// Save settings
export async function saveSettings(settings: any) {
  try {
    await set(settingsRef, {
      ...settings,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error saving settings:', error);
    return false;
  }
}

// Get settings
export async function getSettings() {
  try {
    const snapshot = await get(settingsRef);
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error('Error getting settings:', error);
    return null;
  }
}

// Authentication
export async function loginAdmin(email: string, password: string) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
}

export async function logoutAdmin() {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    return false;
  }
}

// Migrate existing localStorage data to Firebase
export async function migrateLocalDataToFirebase() {
  try {
    console.log('Starting data migration to Firebase...');
    
    // Migrate rooms
    const localRooms = localStorage.getItem('hotel_rooms_data');
    if (localRooms) {
      const rooms = JSON.parse(localRooms);
      for (const room of rooms) {
        await saveRoom(room.room_number, room);
      }
      console.log('Rooms migrated successfully');
    }
    
    // Migrate bookings
    const localBookings = localStorage.getItem('hotel_bookings');
    if (localBookings) {
      const bookings = JSON.parse(localBookings);
      for (const booking of bookings) {
        await saveBooking(booking);
      }
      console.log('Bookings migrated successfully');
    }
    
    // Migrate settings
    const localSettings = localStorage.getItem('hotel_settings');
    if (localSettings) {
      const settings = JSON.parse(localSettings);
      await saveSettings(settings);
      console.log('Settings migrated successfully');
    }
    
    console.log('Migration complete!');
    return true;
  } catch (error) {
    console.error('Error migrating data:', error);
    return false;
  }
}

export { database, auth };
